import React, { useRef } from "react";
import * as d3 from "d3";
import styled from "styled-components";
import { useResize } from "../../utils/useResize";

const detectBrowser = () => {
  const userAgent = window.navigator.userAgent;
  if (userAgent.includes("Firefox")) {
    return "firefox";
  } else if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
    return "chrome";
  } else if (userAgent.includes("Edg")) {
    return "edge";
  }
  return "other";
};

interface IProps {
  score?: string;
  text?: boolean;
}

export const DashOperatingScore = ({ score, text }: IProps) => {
  const componentRef = useRef(null);
  const { width, height } = useResize(componentRef);
  const size = Math.min(width, height);
  const transformedSize = size * 0.6 * 0.9;
  const browser = detectBrowser();

  const safeScore = score ? parseFloat(score) : undefined;
  const difference = 5 - (safeScore || 0);
  const pieChart = d3.pie().sort(null)([safeScore || 0, difference]);
  const slice = d3.arc();

  const getColor = (index: number) => {
    if (index === 0) {
      if (safeScore && safeScore >= 1 && safeScore < 2) return "#D51067";
      if (safeScore && safeScore >= 2 && safeScore < 3) return "#EDBC0C";
      if (safeScore && safeScore >= 3 && safeScore < 4) return "url(#grad3)";
      if (safeScore && safeScore >= 4 && safeScore < 5) return "#4BD041";
      if (safeScore && safeScore >= 5) return "url(#grad2)";
    }
    return "#EEF0FA";
  };

  // Adjusted Y-positioning based on browser
  const scoreTextY = browser === "firefox" ? -transformedSize / 10 : -transformedSize / 7;
  const descriptionTextY = browser === "firefox" ? transformedSize / 5 : transformedSize / 3;

  return (
    <OperatingScoreContainer ref={componentRef}>
      <svg
        viewBox="0 0 200 200"
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          margin: "auto",
          width: "100%",
          height: "100%"
        }}
      >
        <defs>
          <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7C8AFF" stopOpacity={1} />
            <stop offset="100%" stopColor="#948BFF" stopOpacity={1} />
          </linearGradient>
          <linearGradient id="grad3" x1="50%" y1="100%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="#35C99A" stopOpacity={1} />
            <stop offset="100%" stopColor="#EDBC0C" stopOpacity={1} />
          </linearGradient>
        </defs>
        <g transform="translate(100, 100)">
          {pieChart.map((item, index) => (
            <path
              key={index}
              d={slice({
                ...item,
                outerRadius: transformedSize,
                innerRadius: transformedSize - 10,
              }) || ""}
              fill={getColor(index)}
              strokeWidth={2}
              stroke="#FFF"
            />
          ))}
          <circle cx={0} cy={0} r={transformedSize - 10} fill="#F5F6FB" />
          <circle cx={0} cy={0} r={transformedSize - 25} fill="#fff" />

          {/* Score text - use rem units to ensure responsive text */}
          <text
            textAnchor="middle"
            alignmentBaseline="central"
            fontSize={`${transformedSize / 45}rem`}  // Changed to rem-based scaling
            fill="#D51067"
            y={scoreTextY}
          >
            {safeScore?.toFixed(2) || "0.0"}
          </text>

          {/* Description text */}
          <text
            textAnchor="middle"
            fontSize={`${transformedSize / 90}rem`}  // Changed to rem-based scaling
            y={descriptionTextY}
            fill="#A6B3BF"
          >
            {text ? "Weighted" : "Operating"} score
          </text>
        </g>
      </svg>
    </OperatingScoreContainer>
  );
};

const OperatingScoreContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
  }

  @media (max-width: 800px) {
    font-size: 0.8rem;
  }
`;

